"use client"

import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  PlusIcon, 
  ArrowLeft, 
  Loader2, 
  Download, 
  Calendar, 
  Search, 
  FileText, 
  ChevronRight 
} from "lucide-react"
import { BillType, columns } from "./columns"
import { DataTable } from "../../components/data-table"
import BillsService from '../../api/bills.service';
import DataRangeService from '../../api/getdatainrange.service';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { canCreate, canView, validatePermissions } from '@/lib/permissions'

async function getData(setCanLoadMore: any, skip: number, businessId: number): Promise<BillType[]> {
  // Fetch data from your API here.
  const response = await BillsService.getAllBills(businessId, skip);
  console.log("getAllBills response.data", response.data);
  if (response.data.result === 'invalid') {
    //window.location.href = '/logout';
    return [];
  }
  // Notify if exactly 50 bills are returned
  if (response.data.bills.length === 50) {
    setCanLoadMore(true); // No more data to load
  }
  return response.data.bills.map((bill: any) => ({
    id: bill.id,
    publicId: bill.publicId,
    billNumber: bill.billNumber,
    user: bill.user,
    business: bill.business,
    supplier: bill.supplier || bill.customer, // Support both supplier and legacy customer field
    amount: bill.amount,
    currency: bill.currency,
    status: bill.status,
    issueDate: bill.issueDate,
    dueDate: bill.dueDate,
    timezone: bill.timezone,
    dateUpdated: bill.dateUpdated,
    dateCreated: bill.dateCreated,
    billItems: bill.billItems,
    billTaxes: bill.billTaxes,
    receiptNumber: bill.receiptNumber,
    receiptDocumentLink: bill.receiptDocumentLink
  }));
}

async function getDataByDateRange(startDate: Date, endDate: Date, businessId: number): Promise<BillType[]> {
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1; // Months are zero-based
  const startDay = startDate.getDate();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();

  // Fix the API call to use the correct method
  const response = await DataRangeService.getDataUsingDateRange('bills', startYear, startMonth, startDay, endYear, endMonth, endDay);
  if (response.data.result === 'invalid') {
    window.location.href = '/logout';
    return [];
  }
  return response.data.bills.map((bill: any) => ({
    id: bill.id,
    publicId: bill.publicId,
    billNumber: bill.billNumber,
    user: bill.user,
    business: bill.business,
    supplier: bill.supplier || bill.customer, // Support both supplier and legacy customer field
    amount: bill.amount,
    currency: bill.currency,
    status: bill.status,
    issueDate: bill.issueDate,
    dueDate: bill.dueDate,
    timezone: bill.timezone,
    dateUpdated: bill.dateUpdated,
    dateCreated: bill.dateCreated,
    billItems: bill.billItems,
    billTaxes: bill.billTaxes,
    receiptNumber: bill.receiptNumber,
    receiptDocumentLink: bill.receiptDocumentLink
  }));
}

export default function Bills() {
  const [data, setData] = useState<BillType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  // Add skip state for pagination
  const [skip, setSkip] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);

  // Date range states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalBills: 0,
    pendingAmount: 0,
    paidAmount: 0,
    overdueAmount: 0
  });

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    // Validate permissions
    validatePermissions();

    const fetchData = async () => {
      setIsLoading(true);
      const businessId = selectedBusiness?.id;
      if (!businessId) return;
      
      const result = await getData(setCanLoadMore, skip, businessId);
      setIsLoading(false);
      setData(result);
      
      // Calculate summary stats
      const total = result.length;
      const pending = result.filter(bill => bill.status === 'pending')
        .reduce((sum, bill) => sum + bill.amount, 0);
      const paid = result.filter(bill => bill.status === 'paid')
        .reduce((sum, bill) => sum + bill.amount, 0);
      const overdue = result.filter(bill => bill.status === 'overdue')
        .reduce((sum, bill) => sum + bill.amount, 0);
        
      setSummaryStats({
        totalBills: total,
        pendingAmount: pending,
        paidAmount: paid,
        overdueAmount: overdue
      });
    };
    fetchData();
  }, [skip, selectedBusiness]);

  const handleDateRangeSearch = async () => {
    if (startDate && endDate) {
      setIsLoading(true);
      const businessId = selectedBusiness?.id;
      if (!businessId) return;
      
      const result = await getDataByDateRange(startDate, endDate, businessId);
      setIsLoading(false);
      setData(result);
      
      // Update summary stats for filtered data
      const total = result.length;
      const pending = result.filter(bill => bill.status === 'pending')
        .reduce((sum, bill) => sum + bill.amount, 0);
      const paid = result.filter(bill => bill.status === 'paid')
        .reduce((sum, bill) => sum + bill.amount, 0);
      const overdue = result.filter(bill => bill.status === 'overdue')
        .reduce((sum, bill) => sum + bill.amount, 0);
        
      setSummaryStats({
        totalBills: total,
        pendingAmount: pending,
        paidAmount: paid,
        overdueAmount: overdue
      });
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bills");
    const currentDateTime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    XLSX.writeFile(workbook, `Bills_${currentDateTime}.xlsx`);
  };

  const filterByStatus = (status: string) => {
    setActiveTab(status);
    if (status === "all") return;
    
    const filteredData = data.filter(bill => 
      bill.status.toLowerCase() === status.toLowerCase()
    );
    
    // Update the table with filtered data
    // This would require modifying the DataTable component to accept filtered data
  };

  // Check if user has permissions for certain operations
  const hasCreatePermission = canCreate('Bills');
  const hasExportPermission = canView('Bills'); // Some systems use separate export permission

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Bills</h1>
            <p className="text-slate-500 mt-1">Manage and track your business expenses</p>
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="outline" 
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {hasCreatePermission && (
              <Button 
                onClick={() => window.location.href = '/bills/create'} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <PlusIcon className="w-4 h-4 mr-2" /> Create Bill
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Bills</p>
                  <h3 className="text-2xl font-bold text-slate-900 mt-1">{summaryStats.totalBills}</h3>
                </div>
                <div className="bg-blue-50 p-3 rounded-full">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Pending</p>
                  <h3 className="text-2xl font-bold text-amber-600 mt-1">
                    ${summaryStats.pendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h3>
                </div>
                <div className="bg-amber-50 p-3 rounded-full">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Paid</p>
                  <h3 className="text-2xl font-bold text-emerald-600 mt-1">
                    ${summaryStats.paidAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h3>
                </div>
                <div className="bg-emerald-50 p-3 rounded-full">
                  <Download className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Overdue</p>
                  <h3 className="text-2xl font-bold text-rose-600 mt-1">
                    ${summaryStats.overdueAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h3>
                </div>
                <div className="bg-rose-50 p-3 rounded-full">
                  <Calendar className="w-6 h-6 text-rose-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Actions */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium text-slate-800">Filters & Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="date-filter" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="date-filter">Date Filter</TabsTrigger>
                <TabsTrigger value="status-filter">Status Filter</TabsTrigger>
                {hasExportPermission && (
                  <TabsTrigger value="export">Export</TabsTrigger>
                )}
              </TabsList>
              
              <TabsContent value="date-filter">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-slate-500" />
                    <label className="text-sm font-medium text-slate-700 mr-2" htmlFor="startDate">From:</label>
                    <Input 
                      id="startDate" 
                      type="date" 
                      onChange={(e: any) => setStartDate(new Date(e.target.value))} 
                      className="w-40 border-slate-300 focus:border-blue-500 focus:ring-blue-500" 
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-slate-500" />
                    <label className="text-sm font-medium text-slate-700 mr-2" htmlFor="endDate">To:</label>
                    <Input 
                      id="endDate" 
                      type="date" 
                      onChange={(e: any) => setEndDate(new Date(e.target.value))} 
                      className="w-40 border-slate-300 focus:border-blue-500 focus:ring-blue-500" 
                    />
                  </div>
                  
                  <Button 
                    onClick={handleDateRangeSearch} 
                    className="bg-blue-600 hover:bg-blue-700 text-white ml-auto"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Apply Filter
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="status-filter">
                <div className="flex flex-wrap gap-2">
                  <Badge 
                    variant={activeTab === "all" ? "default" : "outline"} 
                    className="cursor-pointer px-3 py-1 text-sm"
                    onClick={() => filterByStatus("all")}
                  >
                    All
                  </Badge>
                  <Badge 
                    variant={activeTab === "pending" ? "default" : "outline"} 
                    className="cursor-pointer px-3 py-1 text-sm bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200"
                    onClick={() => filterByStatus("pending")}
                  >
                    Pending
                  </Badge>
                  <Badge 
                    variant={activeTab === "paid" ? "default" : "outline"} 
                    className="cursor-pointer px-3 py-1 text-sm bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200"
                    onClick={() => filterByStatus("paid")}
                  >
                    Paid
                  </Badge>
                  <Badge 
                    variant={activeTab === "overdue" ? "default" : "outline"} 
                    className="cursor-pointer px-3 py-1 text-sm bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-200"
                    onClick={() => filterByStatus("overdue")}
                  >
                    Overdue
                  </Badge>
                </div>
              </TabsContent>
              
              {hasExportPermission && (
                <TabsContent value="export">
                  <div className="flex items-center">
                    <Button 
                      onClick={exportToExcel} 
                      variant="outline" 
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export to Excel
                    </Button>
                    <p className="text-sm text-slate-500 ml-4">Download all bills data as an Excel spreadsheet</p>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <DataTable columns={columns} data={data} section="bills" isLoading={isLoading} />
            
            {canLoadMore && (
              <div className="flex justify-center p-4 border-t border-slate-200 bg-slate-50">
                <Button 
                  onClick={() => setSkip(skip + 50)} 
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ChevronRight className="w-4 h-4 mr-2" />
                  )}
                  Load More
                  <span className="ml-2 text-xs text-slate-500">
                    Showing {skip + 1} to {skip + data.length}
                  </span>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
